<template>
  <div>
    <BeatLoaderPrimary
      v-if="isConfiguring"
      :style="style.loader"
    />
    <router-view
      v-else
      :key="$route.fullPath"
      :authorized="authorized"
      :state.sync="state"
      @handle-error="handleError"
    />
  </div>
</template>

<script>
  import ApiEmailAvailability from '@js/api/email-availability'
  import ApiEmailVerificationCodeEmail from '@js/api/email-verification-code-email'
  import ApiPasswordResetCodeEmail from '@js/api/password-reset-code-email'
  import ApiSsoAuthorizationUrl from '@js/api/sso-authorization-url'
  import ApiTenantInvitationCodeEmail from '@js/api/tenant-invitation-code-email'
  import ApiTenantInvitationCodeTenant from '@js/api/tenant-invitation-code-tenant'
  import BeatLoaderPrimary from '@modules/v2/loader/beat-loader-primary'
  import HttpStatus from 'http-status-codes'
  import { isObject } from '@js/data-utils'
  import { mapGetters } from 'vuex'
  import { snakeKeyToCamelKeyRecursive } from '@js/str-utils'

  export default {
    name: 'AuthView',
    components: {
      BeatLoaderPrimary,
    },
    data: function () {
      return {
        isConfiguring: true,
        state: {
          email: '',
          emailAvailability: {},
          emailVerificationCode: '',
          linkIsExpired: false,
          passwordResetCode: '',
          ssoAuthorizationUrl: '',
          ssoCode: '',
          tenant: {},
          tenantInvitationCode: '',
        },
        style: {
          loader: {
            height: '100vh',
            width: '100%',
          },
        },
      };
    },
    created: async function () {
      await this.parseQueryParams();

      if (this.isInSsoWorkflow) {
        await this.parseSsoQueryParams();
      }

      if (!this.state.email) {
        await this.fetchEmailByCode();
      }

      if (this.state.email) {
        await this.fetchEmailAvailability();

        if (!this.isInSsoWorkflow) {
          await this.fetchSsoAuthorizationUrl();

          if (this.state.ssoAuthorizationUrl) {
            await this.redirectToSsoAuthorizationUrl();
          }
        }
      }

      if (this.state.tenantInvitationCode) {
        await this.fetchTenantInvitationCodeTenant();
      }

      if (this.state.linkIsExpired && this.$route.name !== 'expired-link') {
        await this.$router.push({
          name: 'expired-link',
          query: this.$route.query,
        });
      }

      this.isConfiguring = await false;
    },
    computed: {
      ...mapGetters('auth', ['refreshTokenExpired']),
      authorized: function () {
        return !this.refreshTokenExpired;
      },
      isInSsoWorkflow: function () {
        return (
          this.$route.path.includes('/sso/') ||
          this.state.ssoCode.length > 0
        );
      },
    },
    methods: {
      fetchEmailAvailability: async function () {
        try {
          const payload = await {
            email: this.state.email,
          };
          const api = await new ApiEmailAvailability();
          const res = await api.post(payload);
          this.state.emailAvailability = await res.data;
        } catch (error) {
          this.handleError(error, 'Failed to fetch email availability.');
        }
      },
      fetchEmailByCode: async function () {
        if (this.state.emailVerificationCode) {
          await this.fetchEmailByEmailVerificationCode(
            this.state.emailVerificationCode);
        } else if (this.state.passwordResetCode) {
          await this.fetchEmailByPasswordResetCode(this.state.passwordResetCode);
        } else if (this.state.tenantInvitationCode) {
          await this.fetchEmailByTenantInvitationCode(this.state.tenantInvitationCode);
        }
      },
      fetchEmailByEmailVerificationCode: async function (emailVerificationCode) {
        try {
          const payload = await {
            verificationCode: emailVerificationCode,
          };
          const api = await new ApiEmailVerificationCodeEmail();
          const res = await api.post(payload);
          this.state.email = await res.data.email;
          if (res.data.email.length === 0) {
            throw res.data.message;
          }
        } catch (error) {
          if (error.response.status === HttpStatus.GONE) {
            this.state.linkIsExpired = true;
          } else {
            this.handleError(error, 'Failed to fetch email.');
          }
        }
      },
      fetchEmailByPasswordResetCode: async function (passwordResetCode) {
        try {
          const payload = await {
            resetCode: passwordResetCode,
          };
          const api = await new ApiPasswordResetCodeEmail();
          const res = await api.post(payload);
          this.state.email = await res.data.email;
          if (res.data.email.length === 0) {
            throw res.data.message;
          }
        } catch (error) {
          if (error.response.status === HttpStatus.GONE) {
            this.state.linkIsExpired = true;
          } else {
            this.handleError(error, 'Failed to fetch email.');
          }
        }
      },
      fetchEmailByTenantInvitationCode: async function (tenantInvitationCode) {
        try {
          const payload = await {
            invitationCode: tenantInvitationCode,
          };
          const api = await new ApiTenantInvitationCodeEmail();
          const res = await api.post(payload);
          this.state.email = await res.data.email;
          if (res.data.email.length === 0) {
            throw res.data.message;
          }
        } catch (error) {
          if (error.response.status === HttpStatus.GONE) {
            this.state.linkIsExpired = true;
          } else {
            this.handleError(error, 'Failed to fetch email.');
          }
        }
      },
      fetchSsoAuthorizationUrl: async function () {
        try {
          const payload = {
            email: this.state.email,
            verificationCode: this.state.emailVerificationCode,
            invitationCode: this.state.tenantInvitationCode,
          };
          const api = await new ApiSsoAuthorizationUrl();
          const res = await api.post(payload);
          this.state.ssoAuthorizationUrl = await res.data.authorizationUrl;
        } catch (error) {
          this.handleError(error, 'Failed to fetch SSO URL.');
        }
      },
      fetchTenantInvitationCodeTenant: async function () {
        try {
          const payload = await {
            invitationCode: this.state.tenantInvitationCode,
          };
          const api = await new ApiTenantInvitationCodeTenant();
          const res = await api.post(payload);
          this.state.tenant = await res.data;
        } catch (error) {
          this.handleError(error, 'Failed to fetch workspace.');
        }
      },
      handleError: function (error, errorMessage, redirectTo = { name: 'login' }) {
        console.error(error);
        this.$store.commit('notification/setWarning', errorMessage);
        this.$router.push(redirectTo);
      },
      parseQueryParam: function (query, queryKey, state, stateKey) {
        if (
          !isObject(query) ||
          !query[queryKey] ||
          !isObject(state)
        ) {
          return;
        }

        state[stateKey] = decodeURIComponent(query[queryKey]);
      },
      parseQueryParams: function () {
        Object.entries({
          code: 'ssoCode',
          email: 'email',
          invitation_code: 'tenantInvitationCode',
          password_reset_code: 'passwordResetCode',
          verification_code: 'emailVerificationCode',
        }).forEach(([queryKey, stateKey]) => {
          this.parseQueryParam(this.$route?.query, queryKey, this.state, stateKey);
        });
      },
      parseSsoQueryParams: function () {
        if (this.$route?.query?.state) {
          const stateInQuery = (
            JSON.parse(decodeURIComponent(this.$route.query.state))
          );
          Object.entries({
            email: 'email',
            verification_code: 'emailVerificationCode',
            invitation_code: 'tenantInvitationCode',
          }).forEach(([queryKey, stateKey]) => {
            this.parseQueryParam(stateInQuery, queryKey, this.state, stateKey);
          });
        }
      },
      redirectToSsoAuthorizationUrl: async function () {
        const params = await { state: this.state };
        await this.$router.push({ name: 'sso-redirect', params: params });
      },
    },
  };
</script>
